/* eslint-disable jsx-a11y/media-has-caption */

import React, {
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import MediaControls from './MediaControls';
import track from '../../util/track';

const AudioWrapper = styled.div`
  width: 100%;
  max-width: 200px;
  float: left;
  margin-left: calc((100vw - 680px) / 2);
  margin-right: 40px;
  margin-bottom: 1rem;  
  margin-top: 0.75rem;
  position: relative;
  position: relative;
  z-index: 101;
  @media (max-width: 740px) {
    margin-left: 20px;
    max-width: 200px;
  }
  .audio__header {
    font-size: 0.875rem;
    line-height: 1rem;
    color: white;
    span {
      color: var(--custom-red);
    }
  }
`;

const AudioBlock = ({ data, playing, setPlaying }) => {
  const audioRef = useRef(null); // audio element
  const [audioDuration, setAudioDuration] = useState(0);
  const [audioTime, setAudioTime] = useState(0);
  const progressRef = useRef(null);

  const [audioVersionStarted, setAudioVersionStarted] = useState(false);
  const [audioEnded, setAudioEnded] = useState(false);

  const trackAudioVersion = (audioStarted) => {
    if (audioStarted === false) {
      track('bg-mag-sex-trafficking-audio-started');
    }
  };

  // ---------------------------------------------------------------
  // metaDataLoaded
  // ---------------------------------------------------------------
  // runs when the audio file's metadata is loaded
  // updates audio data with duration of audio file

  const parseMetaData = () => {
    setAudioDuration(audioRef.current.duration);
  };

  // ---------------------------------------------------------------
  // timeUpdate
  // ---------------------------------------------------------------
  // runs when the currenttime of the audio changes and updates the audio object
  // with the currentime of the audio file

  const parseTimeUpdate = () => {
    const { currentTime } = audioRef.current;
    if (audioRef.current.ended) {
      if (!audioEnded) {
        track('bg-mag-sex-trafficking-audio-completed');
        setAudioEnded(true);
      }
      audioRef.current.pause();
      setPlaying('');
    }
    setAudioTime(currentTime);
  };

  // ---------------------------------------------------------------
  // play/pause audio function
  // ---------------------------------------------------------------
  // controls audio playback and pause depending on the current audio playing state

  const playAudio = useCallback(() => {
    if (playing === '' || playing !== 'audio-version') {
      audioRef.current.play();
      setPlaying('audio-version');
      trackAudioVersion(audioVersionStarted);
      setAudioVersionStarted(true);
    } else {
      audioRef.current.pause();
      setPlaying('');
    }
  }, [audioRef.current, playing]);

  useEffect(() => {
    if (playing !== 'audio-version') {
      audioRef.current.pause();
    }
  }, [playing]);

  // ---------------------------------------------------------------
  // range slider on change callback
  // ---------------------------------------------------------------
  // updates audio file's current time when range slider is changed

  const changeTime = useCallback(() => {
    audioRef.current.currentTime = progressRef.current.value;
  }, []);

  const audioPathMP3 = 'https://apps.bostonglobe.com/magazine/2022/07/kate-price-audio/vignette4.mp3';
  const audioPathOGG = 'https://apps.bostonglobe.com/magazine/2022/07/kate-price-audio/vignette4.ogg';

  return (
    <AudioWrapper>
      <audio
        ref={audioRef}
        onLoadedMetadata={() => parseMetaData()}
        preload="true"
        onTimeUpdate={() => parseTimeUpdate()}
      >
        <source src={audioPathMP3} type="audio/mp3" />
        <source src={audioPathOGG} type="audio/ogg" />
      </audio>

      <h6 className="bentonsanscond-n6 audio__header"><span className="related--label bentonsanscond-n6">LISTEN: </span>{data.audioVersion}</h6>

      <MediaControls
        playMedia={playAudio}
        maxDuration={audioDuration || 0}
        currentTime={audioTime || 0}
        changeTime={changeTime}
        playing={playing}
        progressRef={progressRef}
        mediaId="audio-version"
      />
    </AudioWrapper>
  );
};

export default AudioBlock;

AudioBlock.propTypes = {
  data: PropTypes.shape().isRequired,
  playing: PropTypes.string.isRequired,
  setPlaying: PropTypes.func.isRequired,
};
